import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  useGetAdminDropoffLocations,
  useCreateDropoffLocation,
  useUpdateDropoffLocation,
  useDeleteDropoffLocation,
  type AdminDropoffLocation,
} from "@/api/admin-carrier-dropoff-locations";
import { MapPin, Plus, Trash2, Pencil } from "lucide-react";

interface CarrierDropoffLocationsSectionProps {
  carrierId: string;
  carrierName: string;
}

/**
 * Manage CarrierDropoffLocation rows per carrier — the "you can drop your
 * package here" addresses surfaced in the customer create-order flow.
 *
 * Server: ezx-server PR #225. Mirrors the CarrierServicesSection pattern
 * (inline table + add/edit dialog).
 */
export function CarrierDropoffLocationsSection({
  carrierId,
  carrierName,
}: CarrierDropoffLocationsSectionProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<AdminDropoffLocation | null>(null);

  const query = useGetAdminDropoffLocations(carrierId, {
    includeInactive: true,
  });

  const updateMutation = useUpdateDropoffLocation({
    onSuccess: () => {
      toast.success("Dropoff updated");
    },
    onError: (err) =>
      toast.error("Update failed", { description: err.message }),
  });

  const deleteMutation = useDeleteDropoffLocation({
    onSuccess: () => toast.success("Dropoff removed"),
    onError: (err) =>
      toast.error("Delete failed", { description: err.message }),
  });

  const rows = query.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Dropoff Locations
          </div>
          <p className="text-xs text-muted-foreground">
            Addresses where customers can drop packages for this carrier.
            Surfaced in the create-order flow, distance-sorted from sender.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setAddOpen(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add location
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table className="min-w-[640px]">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center text-muted-foreground py-4"
                >
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!query.isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center text-muted-foreground py-6"
                >
                  No dropoff locations yet. Click "Add location" to create one.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} className={!r.active ? "opacity-50" : ""}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-xs">
                  {r.address.line1}
                  {r.address.line2 && <span>, {r.address.line2}</span>}
                  <div className="text-[11px] text-muted-foreground">
                    {r.address.city}, {r.address.province}{" "}
                    {r.address.postalCode ?? ""}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={r.active}
                      onCheckedChange={(checked) =>
                        updateMutation.mutate({
                          id: r.id,
                          input: { active: checked },
                        })
                      }
                      disabled={updateMutation.isPending}
                      aria-label={r.active ? "Disable" : "Enable"}
                    />
                    <Badge variant="outline" className="text-[10px]">
                      {r.active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setEditing(r)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => deleteMutation.mutate(r.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <DropoffLocationDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        carrierId={carrierId}
        carrierName={carrierName}
      />
      {editing && (
        <DropoffLocationDialog
          open
          onClose={() => setEditing(null)}
          carrierId={carrierId}
          carrierName={carrierName}
          existing={editing}
        />
      )}
    </div>
  );
}

interface DropoffLocationDialogProps {
  open: boolean;
  onClose: () => void;
  carrierId: string;
  carrierName: string;
  existing?: AdminDropoffLocation;
}

interface FormState {
  name: string;
  line1: string;
  line2: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
}

function toFormState(existing?: AdminDropoffLocation): FormState {
  if (!existing) {
    return {
      name: "",
      line1: "",
      line2: "",
      city: "",
      province: "ON",
      country: "Canada",
      postalCode: "",
    };
  }
  return {
    name: existing.name,
    line1: existing.address.line1,
    line2: existing.address.line2 ?? "",
    city: existing.address.city,
    province: existing.address.province,
    country: existing.address.country,
    postalCode: existing.address.postalCode ?? "",
  };
}

function DropoffLocationDialog({
  open,
  onClose,
  carrierId,
  carrierName,
  existing,
}: DropoffLocationDialogProps) {
  const [form, setForm] = useState<FormState>(toFormState(existing));

  const createMutation = useCreateDropoffLocation({
    onSuccess: (data) => {
      toast.success("Dropoff added", {
        description: `${data.name} added to ${carrierName}`,
      });
      onClose();
    },
    onError: (err) =>
      toast.error("Failed to add", { description: err.message }),
  });
  const updateMutation = useUpdateDropoffLocation({
    onSuccess: () => {
      toast.success("Dropoff updated");
      onClose();
    },
    onError: (err) =>
      toast.error("Update failed", { description: err.message }),
  });

  const submit = () => {
    if (!form.name.trim() || !form.line1.trim() || !form.city.trim()) {
      toast.error("Missing fields", {
        description: "Name, street, and city are required.",
      });
      return;
    }
    const input = {
      name: form.name.trim(),
      address: {
        line1: form.line1.trim(),
        line2: form.line2.trim() ? form.line2.trim() : null,
        city: form.city.trim(),
        province: form.province.trim(),
        country: form.country.trim(),
        postalCode: form.postalCode.trim() ? form.postalCode.trim() : null,
      },
    };
    if (existing) {
      updateMutation.mutate({ id: existing.id, input });
    } else {
      createMutation.mutate({ carrierId, input });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {existing
              ? "Edit dropoff location"
              : `Add dropoff to ${carrierName}`}
          </DialogTitle>
          <DialogDescription>
            Address is auto-geocoded so distance sorting works for customers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="dl-name">Name *</Label>
            <Input
              id="dl-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Downtown depot"
            />
          </div>
          <div>
            <Label htmlFor="dl-line1">Street *</Label>
            <Input
              id="dl-line1"
              value={form.line1}
              onChange={(e) => setForm({ ...form, line1: e.target.value })}
              placeholder="100 Bloor St W"
            />
          </div>
          <div>
            <Label htmlFor="dl-line2">Unit / Suite</Label>
            <Input
              id="dl-line2"
              value={form.line2}
              onChange={(e) => setForm({ ...form, line2: e.target.value })}
              placeholder="Suite 200"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="dl-city">City *</Label>
              <Input
                id="dl-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Toronto"
              />
            </div>
            <div>
              <Label htmlFor="dl-province">Province *</Label>
              <Input
                id="dl-province"
                value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
                placeholder="ON"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="dl-postal">Postal code</Label>
              <Input
                id="dl-postal"
                value={form.postalCode}
                onChange={(e) =>
                  setForm({ ...form, postalCode: e.target.value.toUpperCase() })
                }
                placeholder="M5S 2V6"
              />
            </div>
            <div>
              <Label htmlFor="dl-country">Country</Label>
              <Input
                id="dl-country"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                placeholder="Canada"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending ? "Saving…" : existing ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
